import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Eye,
  Filter,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  task_id: string | null;
  supplier_order_id: string | null;
  return_request_id: string | null;
  created_at: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const NOTIFICATION_TYPES = [
  '',
  'escalation',
  'order_failure',
  'tracking_issue',
  'return_dispute',
  'price_alert',
  'inventory_alert',
  'system',
  'info',
];

const typeBadge: Record<string, { variant: 'red' | 'yellow' | 'blue' | 'green' | 'gray'; label: string }> = {
  escalation: { variant: 'red', label: 'Escalation' },
  order_failure: { variant: 'red', label: 'Order Failure' },
  tracking_issue: { variant: 'yellow', label: 'Tracking Issue' },
  return_dispute: { variant: 'yellow', label: 'Return Dispute' },
  price_alert: { variant: 'yellow', label: 'Price Alert' },
  inventory_alert: { variant: 'blue', label: 'Inventory Alert' },
  system: { variant: 'blue', label: 'System' },
  info: { variant: 'gray', label: 'Info' },
};

export default function AgentNotifications() {
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 25, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [markingAll, setMarkingAll] = useState(false);
  const [markingBatch, setMarkingBatch] = useState(false);

  const fetchNotifications = async (page = 1, silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '25');
      if (typeFilter) params.set('type', typeFilter);
      if (unreadOnly) params.set('unread', 'true');
      const res = await api.get<{ notifications: Notification[]; pagination: PaginationData }>(
        `/admin/agent/notifications?${params.toString()}`
      );
      setNotifications(res.notifications);
      setPagination(res.pagination);
      setSelected(new Set());
    } catch (err) {
      if (!silent) {
        addToast(err instanceof Error ? err.message : 'Failed to load notifications', 'error');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
  }, [typeFilter, unreadOnly]);

  useEffect(() => {
    const interval = setInterval(() => fetchNotifications(pagination.page, true), 30_000);
    return () => clearInterval(interval);
  }, [pagination.page, typeFilter, unreadOnly]);

  const handleMarkRead = async (id: string) => {
    try {
      await api.patch(`/admin/agent/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to mark as read', 'error');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await api.post('/admin/agent/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      addToast('All notifications marked as read', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to mark all as read', 'error');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkBatchRead = async () => {
    if (selected.size === 0) return;
    try {
      setMarkingBatch(true);
      await api.post('/admin/agent/notifications/mark-batch-read', { ids: [...selected] });
      setNotifications((prev) => prev.map((n) => (selected.has(n.id) ? { ...n, read: true } : n)));
      setSelected(new Set());
      addToast(`${selected.size} notifications marked as read`, 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to mark as read', 'error');
    } finally {
      setMarkingBatch(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === notifications.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notifications.map((n) => n.id)));
    }
  };

  const handleView = (n: Notification) => {
    if (!n.read) handleMarkRead(n.id);
    if (n.task_id) navigate(`/admin/agent/tasks/${n.task_id}`);
    else if (n.supplier_order_id) navigate(`/admin/supplier-orders`);
  };

  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">Alerts and escalations from the AI agent and pipeline.</p>
        </div>
        <button
          onClick={handleMarkAllRead}
          disabled={markingAll}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <CheckCheck className="w-4 h-4" />
          Mark All Read
        </button>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {NOTIFICATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t === '' ? 'All types' : (typeBadge[t]?.label ?? t)}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Unread only</span>
          </label>
          {selected.size > 0 && (
            <button
              onClick={handleMarkBatchRead}
              disabled={markingBatch}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors disabled:opacity-50"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark {selected.size} as Read
            </button>
          )}
        </div>
      </Card>

      {/* Notification List */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No notifications</h3>
            <p className="text-gray-500 text-sm">
              {unreadOnly || typeFilter
                ? 'No notifications match your filters.'
                : "You'll see alerts here when the agent needs your attention."}
            </p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selected.size === notifications.length && notifications.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-xs">
                    Message
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {notifications.map((n) => {
                  const badge = typeBadge[n.type] ?? { variant: 'gray' as const, label: n.type };
                  return (
                    <tr
                      key={n.id}
                      className={n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/30 hover:bg-blue-50/50'}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(n.id)}
                          onChange={() => toggleSelect(n.id)}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${n.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                          {n.title}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <span className="text-sm text-gray-500 truncate block">
                          {n.message.length > 100 ? n.message.slice(0, 100) + '...' : n.message}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-500">{formatTime(n.created_at)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleView(n)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchNotifications(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchNotifications(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
